import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownardsHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Newtownards | Serving Newtownards from Belfast Clinic</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Newtownards from our Belfast clinic (18 miles). Commuter stress, work-life balance, retail worker anxiety, parenting support. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Newtownards, hypnotherapist Newtownards County Down, anxiety treatment Newtownards, commuter stress Ards Peninsula, work-life balance Newtownards" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownards-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Newtownards | Serving Newtownards from Belfast Clinic" />
        <meta property="og:description" content="Professional hypnotherapy serving Newtownards from our Belfast clinic. Commuter stress, work-life balance, retail worker support." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Newtownards",
            "description": "CNHC registered hypnotherapy serving Newtownards, County Down from our Belfast clinic. Specializing in commuter stress, work-life balance, retail worker anxiety, parenting stress for dual-income families, and general anxiety management.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownards-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Newtownards",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Down"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Commuter Stress Management",
              "Work-Life Balance Support",
              "Retail Worker Anxiety",
              "Parenting Stress for Dual-Income Families",
              "Smoking Cessation"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Newtownards
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Based in Belfast, serving Newtownards and the Ards Peninsula
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">A straightforward drive</span> from Newtownards via the A20 • <span className="font-semibold">25-30 min</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Familiar route for Belfast commuters • Online sessions for maximum flexibility</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Newtownards and East Down
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                While I don't have a physical clinic in Newtownards, I'm delighted to serve this vibrant East Down community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. For Newtownards residents, accessing professional hypnotherapy is remarkably straightforward - the A20 runs straight into Belfast and the clinic sits just south of the city centre on the Ormeau Road, so the journey takes only 25-30 minutes. This is a very manageable distance for regular weekly sessions, particularly since thousands of Newtownards residents already make this exact commute daily for work in Belfast.
              </p>
              <p>
                Newtownards is one of Northern Ireland's largest towns, with a population of approximately 29,000 people. Its character is defined by a unique dual identity: a traditional market town with historic roots, and simultaneously a modern commuter hub serving Belfast's eastern suburbs. The town's excellent road links via the A20, proximity to Belfast, and location at the gateway to the Ards Peninsula have transformed Newtownards into a significant residential and retail center. The Ards Shopping Centre and surrounding retail parks employ hundreds of local residents, while many more commute daily to Belfast for professional employment.
              </p>
              <p>
                This creates a distinctive demographic profile: busy dual-income families juggling Belfast employment with Newtownards home life, retail and service workers managing customer-facing pressures at shopping centers, parents coordinating complex school runs around work schedules, and a community experiencing the specific stresses of modern commuter town living. Whether you're struggling with work-life balance as a Belfast commuter, managing retail sector anxiety, dealing with parenting stress when both partners work full-time, or experiencing general anxiety and sleep problems from the pace of busy family life, hypnotherapy offers evidence-based support tailored to the realities of Newtownards living.
              </p>
              <p>
                The A20 route you likely already know well becomes a pathway to professional mental health support that integrates seamlessly with your existing routine. Many Newtownards clients schedule sessions before or after their Belfast work day, making efficient use of commuting time rather than adding extra journeys to an already packed schedule.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Newtownards Residents Choose Belfast Clinic */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Why Newtownards Residents Choose Our Belfast Clinic
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Car className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Already Part of Your Commute Route
                    </h3>
                    <p className="text-gray-700">
                      If you're one of the many Newtownards residents who commute to Belfast for work, you already know the A20 route into the city intimately. Adding a therapy session before or after work requires no new navigation - just 25-30 minutes on roads you drive daily, making regular appointments easy to maintain.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Manageable Distance for Weekly Sessions
                    </h3>
                    <p className="text-gray-700">
                      Belfast is close enough for consistent weekly therapy without feeling like a major expedition. This isn't a distant journey requiring extensive planning - it's a reasonable drive that thousands of Newtownards residents make daily anyway.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Flexible Scheduling Around Busy Lives
                    </h3>
                    <p className="text-gray-700">
                      Clinic days and times vary week to week, and early evening appointments are available, so sessions can be fitted around work schedules, school runs, and retail shifts. Book a slot that aligns with your Belfast commute, or choose a quieter time when traffic is lighter.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <MapPin className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Online Option When Travel Isn't Feasible
                    </h3>
                    <p className="text-gray-700">
                      School run clash? Bad weather on the A20? Sick child at home? Online video sessions provide the same quality hypnotherapy without leaving Newtownards - perfect for maintaining consistency when in-person visits aren't practical that particular week.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Newtownards Community */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Services Tailored to Newtownards' Commuter Community
            </h2>
            <p className="text-lg text-gray-700 mb-12">
              Living in Newtownards brings unique stressors that differ from both rural communities and urban Belfast itself. The daily commute to Belfast, retail sector pressures at Ards Shopping Centre, juggling family responsibilities when both parents work, coordinating school runs around employment, and the challenge of maintaining work-life balance when your job is 18 miles from home - these are the modern realities of East Down commuter town life. Hypnotherapy addresses these specific challenges with evidence-based techniques.
            </p>

            <div className="space-y-10">
              {/* Commuter Stress and Work-Life Balance */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Commuter Stress and Work-Life Balance Management
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    For thousands of Newtownards residents who commute to Belfast for work, the daily journey represents significant time away from family and personal life - typically 45-60 minutes round trip in good traffic, often longer during rush hour. Over a year, this amounts to around 200-250 hours just sitting in the car or bus. The cumulative effect creates a specific form of stress: guilt about missing children's activities, exhaustion from early starts (leaving Newtownards by 7:30am to reach Belfast by 9am), anxiety about A20 traffic delays making you late, frustration at petrol costs and parking fees, and the persistent feeling that there's never enough time for yourself, your partner, or genuine family connection beyond the evening scramble of dinner, homework, and bedtime.
                  </p>
                  <p>
                    Many Newtownards commuters describe feeling like they live two separate lives - the work persona in Belfast and the exhausted parent/partner who arrives home too drained to be fully present. The mental load is constant: worrying about work while at home, worrying about home responsibilities while at work, and never feeling you're doing either adequately. Weekends become catch-up time for housework and chores rather than rest, perpetuating the exhaustion cycle.
                  </p>
                  <p>
                    Hypnotherapy helps commuters develop healthier boundaries and reclaim mental space. Techniques include: reframing your commute as valuable transition time between roles rather than "lost time," reducing anticipatory anxiety about traffic (which steals energy before you even leave), establishing evening routines that prioritize family connection over collapsing in front of the TV, managing guilt about work-home balance with realistic rather than perfect expectations, building resilience against the exhaustion of dual-location living, and improving sleep quality so you wake refreshed rather than already depleted.
                  </p>
                  <p>
                    Several Newtownards clients have found that scheduling hypnotherapy sessions at 5pm or 6pm in Belfast, on their way home from work, creates a valuable buffer between work mode and home mode. Rather than rushing straight home to collapse, they invest 60 minutes in mental health support that improves their entire week. The session becomes part of their routine, and the commute home afterwards feels calmer and more intentional.
                  </p>
                </div>
              </div>

              {/* Retail and Service Worker Anxiety */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Retail and Service Worker Anxiety Support
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    The Ards Shopping Centre and surrounding retail parks in Newtownards employ hundreds of local residents in customer-facing roles - retail assistants, supervisors, café workers, hairdressers, beauticians, and service staff. While these jobs provide convenient local employment without the Belfast commute, they come with distinct and often underestimated pressures: dealing with demanding or rude customers while maintaining professional composure, meeting sales targets that may feel unrealistic, standing on your feet all day leading to physical exhaustion, working weekends and late evenings that disrupt family time and social life, the intense seasonal pressure of Christmas retail, and the emotional labor of performing cheerfulness regardless of how you actually feel.
                  </p>
                  <p>
                    Retail workers often experience anticipatory anxiety in the hours before a shift, especially before busy weekends or seasonal rushes. The physical symptoms are common: tension headaches, jaw clenching, stomach problems, difficulty sleeping the night before a shift. There's also the emotional exhaustion of constant emotional suppression - the requirement to smile and be helpful even when customers are aggressive, dismissive, or treating you as invisible. Over time, this creates a sense of being undervalued despite working extremely hard in essential roles that keep the community functioning.
                  </p>
                  <p>
                    Many retail workers also struggle with the irregular scheduling that makes routine difficult - finishing work at 9pm one day, starting at 8am the next, different days off each week that prevent establishing consistent social plans or family time. The lack of predictability adds to stress and makes self-care challenging.
                  </p>
                  <p>
                    Hypnotherapy provides practical tools specifically for retail professionals: techniques to reduce pre-shift anxiety so you're not dreading work hours before you clock in, strategies to create emotional boundaries with difficult customers (offering compassion without absorbing their negativity), methods to recover quickly from stressful interactions rather than ruminating all day, sleep optimization for workers with irregular schedules, confidence-building to counteract the feeling of being "just a shop assistant," and stress management for the specific pressures of customer service roles.
                  </p>
                  <p>
                    You deserve professional mental health support for the real pressures you face. Retail work is demanding, exhausting, and often undervalued - but your wellbeing matters, and hypnotherapy can help you manage the stress in sustainable ways.
                  </p>
                </div>
              </div>

              {/* Parenting Stress for Dual-Income Families */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Parenting Stress for Dual-Income Families
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Newtownards is home to many dual-income families where both parents work - often one or both commuting to Belfast, or working locally in retail or services. The logistics of managing family life in this context are genuinely complex: coordinating school drop-offs when you both need to leave for Belfast by 7:30am, arranging after-school care or relying on grandparents, managing homework and dinner preparation when you don't get home until 6pm, fitting in children's activities and sports, maintaining the house, and somehow finding time for meaningful family connection beyond the exhausting daily routine.
                  </p>
                  <p>
                    The result is that many parents feel they're constantly "managing logistics" rather than truly parenting. You're coordinating schedules, organizing childcare, supervising homework while cooking dinner, bathing children while mentally planning tomorrow's outfit for work. The mental load is exhausting - one parent (usually mothers) carrying the invisible burden of remembering everything, planning everything, worrying about everything, while also working full-time.
                  </p>
                  <p>
                    Common stressors include: guilt about leaving children with grandparents or childcare while you work, anxiety about missing school events or sports matches because of work commitments, frustration at having no time for self-care (when did you last have 30 minutes just for yourself?), worry about your children's wellbeing while feeling too stretched thin to address concerns properly, resentment toward your partner if you feel you're carrying more of the domestic burden, and the overwhelming pressure to "have it all" - successful career, perfect home, happy children, social life - while feeling you're failing at everything.
                  </p>
                  <p>
                    Hypnotherapy helps parents develop more realistic expectations and release guilt that serves no useful purpose. Specific approaches include: challenging the "perfect parent" narrative and accepting "good enough" parenting, establishing boundaries around work demands (learning to say no without shame), releasing guilt about childcare arrangements (your children are safe and loved), improving sleep quality despite racing thoughts about tomorrow's schedule, managing the mental load more sustainably, reconnecting with why you chose parenthood beyond the logistics treadmill, and building resilience against the constant pressure to do more, be more, achieve more.
                  </p>
                  <p>
                    Sessions can be scheduled flexibly around school hours, during Belfast work hours if you can take time off, in the evening after children are in bed, or online from home when childcare makes leaving the house difficult. The investment in your mental health makes you a calmer, more present parent - which benefits your children far more than another activity or achievement.
                  </p>
                </div>
              </div>

              {/* General Anxiety and Stress */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  General Anxiety and Stress Management
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Beyond specific roles and circumstances, many Newtownards residents experience generalized anxiety - persistent worry that feels disproportionate to actual threats, racing thoughts that won't quiet, physical tension (tight shoulders, clenched jaw, shallow breathing), sleep disturbances (difficulty falling asleep, waking at 3am with mind racing), and the overwhelming feeling that you're barely keeping your head above water. Modern life in a busy commuter town creates particular pressures: financial stress from mortgages and commuting costs, social comparison amplified by social media, packed schedules that leave no breathing room, and the relentless pace of trying to maintain suburban family life while working full-time.
                  </p>
                  <p>
                    Anxiety often manifests physically - headaches, digestive problems, muscle tension, fatigue that isn't relieved by sleep. Many people describe feeling constantly on edge, waiting for something bad to happen, unable to relax even when there's nothing objectively wrong. The worry becomes automatic: worrying about work performance, children's wellbeing, money, health, relationships, whether you locked the car, whether you sent that email, whether you're doing enough, being enough.
                  </p>
                  <p>
                    Hypnotherapy addresses anxiety at the subconscious level where automatic worry patterns are stored, rather than just conscious coping strategies. Clinical hypnosis helps retrain the nervous system's threat response, reducing the "background hum" of constant tension that characterizes generalized anxiety. Techniques include: progressive relaxation to release physical holding patterns accumulated from years of stress, cognitive reframing to challenge catastrophic thinking ("worst case scenario" rumination), visualization to create a felt sense of safety in your nervous system, anchoring techniques to access calm states quickly during stressful moments, and improving sleep architecture so you wake genuinely rested.
                  </p>
                  <p>
                    Research shows hypnotherapy can be as effective as CBT for anxiety disorders, with some clients reporting significant improvement within 4-6 sessions. The approach is particularly helpful for those who find traditional talk therapy too slow or intellectual - hypnotherapy works directly with the emotional brain where anxiety originates, creating change at a deeper level than rational discussion alone.
                  </p>
                </div>
              </div>

              {/* Additional Services */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Sleep Problems and Insomnia
                  </h4>
                  <p className="text-gray-700">
                    Racing thoughts keeping you awake until 2am? Waking at 4am worrying about tomorrow's workload? Early alarm for Belfast commute but can't fall asleep? Hypnotherapy helps quiet mental chatter and retrain your nervous system for restful sleep - essential when you need to be functional for work, parenting, and daily life.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Smoking Cessation and Habit Change
                  </h4>
                  <p className="text-gray-700">
                    Ready to quit smoking? Break stress-eating patterns that have worsened since starting the Belfast commute? Address nail-biting or other anxiety-driven habits? Hypnotherapy targets the subconscious drivers of automatic behaviors - more effective than willpower alone, addressing why you smoke/eat/bite rather than just stopping.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Chronic Pain Management
                  </h4>
                  <p className="text-gray-700">
                    Living with fibromyalgia, back pain from commuter driving, tension headaches, or other persistent pain? Hypnotherapy helps reduce pain intensity and improve quality of life by working with the nervous system's pain processing pathways. NHS-complementary support with evidence base.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Confidence and Self-Esteem
                  </h4>
                  <p className="text-gray-700">
                    Struggling with imposter syndrome at your Belfast job? Social anxiety in Newtownards community? Low self-worth from feeling you're failing at work and parenting? Hypnotherapy helps build genuine confidence from within - not fake-it-till-you-make-it performance, but authentic self-acceptance and quiet self-assurance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Getting to the Belfast Clinic from Newtownards
            </h2>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Car className="w-6 h-6 text-accent-600 mr-3" />
                By Car (Recommended)
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-semibold">Drive time: 25-30 minutes (off-peak), 35-45 minutes (rush hour)</p>
                <p><strong>Route:</strong></p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>From Newtownards town centre, head west on the A20 toward Belfast</li>
                  <li>Continue on the A20 through Dundonald</li>
                  <li>The A20 becomes the Upper Newtownards Road as you approach east Belfast</li>
                  <li>Follow signs for the city centre and cross the river at the Ormeau Bridge</li>
                  <li>Turn onto the Ormeau Road (A24) - the clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ</li>
                </ol>
                <p><strong>Where that is:</strong> South Belfast, on the Ormeau Road, about 10 minutes from the city centre.</p>
                <p className="text-sm italic">Tip: Many Newtownards commuters already know the A20 run into Belfast from daily work travel - the clinic is just the far side of the city centre from there.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Train className="w-6 h-6 text-accent-600 mr-3" />
                By Bus
              </h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>Service:</strong> Translink Ulsterbus operates services from Newtownards to Belfast</p>
                <p><strong>Journey time:</strong> Approximately 35-45 minutes depending on service and traffic</p>
                <p><strong>From Belfast city centre to clinic:</strong> Metro services run out along the Ormeau Road corridor into south Belfast. If you travel by rail instead, Botanic and City Hospital are the closest stations to the clinic, with Lanyon Place serving regional routes</p>
                <p className="text-sm italic">Note: Bus service is less frequent than train options from other towns, so car is generally more convenient for most Newtownards residents, especially those already driving to Belfast for work.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-6 h-6 text-accent-600 mr-3" />
                Online Sessions
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Can't make the journey on a particular week? Childcare clash? Bad weather making the A20 treacherous? <strong>Online video sessions</strong> provide the same professional hypnotherapy without the 18-mile round trip.
                </p>
                <p>
                  Online sessions are particularly helpful for: busy parents juggling childcare and can't leave the house, retail workers with irregular shift patterns making consistent appointment times difficult, anyone managing sessions around unpredictable work commitments, or during poor weather when commuting is challenging.
                </p>
                <p>
                  Sessions conducted via secure video platform - equally effective as in-person for most clients, with maximum flexibility for your Newtownards schedule. Many clients use a hybrid approach: in-person when it suits their routine, online when travel isn't practical that particular week.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Questions from Newtownards Clients
            </h2>
            <div className="space-y-6">

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I add a hypnotherapy session to my Belfast work commute?
                </h3>
                <p className="text-gray-700">
                  Absolutely - this is one of the most popular approaches for Newtownards commuters. Many clients schedule therapy sessions at 5pm or 6pm in Belfast, on their way home from work. This makes efficient use of your commute day (you're already traveling to Belfast) and creates valuable transition time between work mode and home mode. Instead of rushing home to collapse exhausted, you invest 60 minutes in mental health support that improves your entire week. Some clients prefer morning sessions before work starts. Either way, the clinic is easily accessible from your regular commute route.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How far is Newtownards from the Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is a straightforward drive from Newtownards via the A20. Travel time is typically 25-30 minutes off-peak, longer during rush hour. This is a very manageable distance for regular weekly sessions - many Newtownards residents already drive this route daily. The clinic is at 228 Ormeau Road in south Belfast, about 10 minutes beyond the city centre, on the Ormeau Road (A24).
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you specifically work with commuter stress and work-life balance?
                </h3>
                <p className="text-gray-700">
                  Yes, this is one of my most common specializations with Newtownards and other commuter town clients. Work-life balance and commuter stress create unique challenges - time away from family, exhaustion from early starts and late returns, guilt about missing children's activities, difficulty switching off from work mode. We work on reframing commute time, reducing anticipatory anxiety about traffic and delays, managing evening exhaustion so you can be present with family, establishing boundaries around work demands, and improving sleep quality. Many clients find that addressing commuter stress improves not just the journey but their entire experience of work and home life.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I fit appointments around school runs and work schedules?
                </h3>
                <p className="text-gray-700">
                  Yes - I offer flexible scheduling including morning slots (after school drop-off if you're not commuting that day), mid-day during work hours (if you can take time off), and evening appointments (after school pick-up). For dual-income families with complex logistics, online sessions can be particularly helpful - no travel time means you can fit a session during lunch break, between meetings, or after children are in bed without needing to leave Newtownards. The flexibility helps you prioritize mental health without adding impossible demands to an already packed schedule.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can you help with retail worker stress from Ards Shopping Centre?
                </h3>
                <p className="text-gray-700">
                  Yes, retail and service worker support is a key specialization. Working at Ards Shopping Centre or surrounding retail parks brings real pressures - demanding customers, sales targets, emotional labor of maintaining cheerfulness, irregular shift patterns, weekend work disrupting family time. Hypnotherapy helps you develop emotional boundaries with difficult customers (compassion without absorption), reduce pre-shift anxiety, recover quickly from stressful interactions rather than ruminating all day, build confidence in your professional value, and manage the specific stress of customer-facing roles. Your mental health matters regardless of your job title.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What if I can't travel to Belfast every week?
                </h3>
                <p className="text-gray-700">
                  That's completely fine - online video sessions provide the same professional hypnotherapy treatment without any travel. Many Newtownards clients use a hybrid approach: in-person sessions when they're already commuting to Belfast for work or when their schedule allows, and online sessions when travel isn't practical (childcare issues, bad weather on A20, irregular work schedule, exhaustion). The therapeutic effectiveness is equivalent, and the flexibility helps maintain consistency rather than missing sessions when in-person travel is challenging.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies by individual and concern. Most clients notice improvement within 4-6 sessions for issues like anxiety, stress management, or sleep problems. Some concerns (specific phobias, smoking cessation) may respond in 1-3 sessions. Deeper patterns (chronic anxiety, long-term commuter stress, complex work-life balance issues) may benefit from 8-12 sessions. We'll discuss your specific goals during the free initial consultation and create a realistic plan. You're always in control of the pace and can stop whenever you feel you've achieved what you needed.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy covered by health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance plans cover hypnotherapy if provided by a CNHC-registered practitioner (which I am). Check your policy details or contact your insurer to confirm. If not covered, discounted packages are available when you book multiple sessions. Many clients find the investment worthwhile compared to the ongoing costs of stress, poor sleep, anxiety, or unwanted habits - both the financial costs (sick days, reduced productivity) and the human cost to quality of life.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What's the difference between online and in-person hypnotherapy sessions?
                </h3>
                <p className="text-gray-700">
                  The therapy is equally effective - hypnosis works through voice, guidance, and your own subconscious processes, not physical presence in a room. Online sessions offer convenience (no 18-mile round trip), flexibility (easier to fit around work/family/retail shifts), and comfort (therapy from your own space in Newtownards). In-person sessions provide dedicated time away from home distractions, face-to-face connection, and the ritual of "going to therapy" as distinct from daily life. Many clients use both approaches based on their schedule and circumstances each week - whatever maintains consistency and fits your life.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are you qualified and registered?
                </h3>
                <p className="text-gray-700">
                  Yes - I'm registered with the CNHC (Complementary and Natural Healthcare Council), the UK regulatory body for hypnotherapists accredited by the Professional Standards Authority. This requires 450+ hours of clinical training, ongoing professional development, full insurance, and adherence to strict ethical standards. You can verify my registration at www.cnhc.org.uk by searching for my name. I also have 15 years of nursing experience at Belfast City Hospital, bringing a healthcare professional's understanding of medical contexts, mental health, and professional standards to hypnotherapy practice.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What Clients from County Down Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I was drowning in guilt about missing my kids' school events because of my Belfast commute. Sharon helped me develop realistic expectations instead of trying to be a perfect parent while working full-time 18 miles from home. The sessions at 5pm on Wednesdays became part of my routine - I was already in Belfast for work, so it was easy. My anxiety about 'failing' at everything has reduced dramatically."
                </p>
                <p className="text-gray-900 font-semibold">— M.K., Newtownards</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Working at Ards Shopping Centre, the customer service stress was making me dread every shift. Sharon gave me practical tools to set boundaries with difficult customers - I can handle rudeness now without taking it personally or carrying it home. The online sessions fit perfectly around my irregular retail schedule. Game changer."
                </p>
                <p className="text-gray-900 font-semibold">— L.T., Comber</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The commuter stress was destroying my mental health - leaving Newtownards at 7:15am, not home until 6:30pm, exhausted and irritable with my family. Sharon helped me reframe the commute and establish better work boundaries. I actually use the drive time now for decompression instead of just stressing about traffic. Sleep has improved massively too."
                </p>
                <p className="text-gray-900 font-semibold">— D.R., Ards Peninsula</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I quit smoking after 18 years with Sharon's help - took 4 sessions. The hypnotherapy worked where willpower, patches, and vaping had all failed. The drive from Newtownards was nothing compared to finally being free. I'm saving hundreds of pounds a month and can play with my kids without getting breathless. Highly recommend."
                </p>
                <p className="text-gray-900 font-semibold">— J.H., Dundonald</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The generalized anxiety I'd lived with for years has reduced dramatically after 6 sessions. Sharon's approach is professional, evidence-based, and understanding of the specific pressures of modern family life in Newtownards. The Belfast clinic is easy to reach and I've recommended her to three friends already."
                </p>
                <p className="text-gray-900 font-semibold">— S.P., Newtownards</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  );
};

export default NewtownardsHypnotherapy;
